/* 문항이 구하라는 양(넓이, 길이, 기울기, 각)은 호스트의 도구 선택에 맡기지 않고 엔진이 직접 세워 잰다(사용자 지시 2026-10-21: 호스트가 넓이 대신 길이를 재 불성립).
 * 호스트는 값 옮기기에서 「무엇을 재는가」만 선언한다(target). 엔진이 화면의 개체 이름과 좌표로 호출을 만든다 */
import { readMeasurements } from './measure';
import type { MathcoreApp, ToolResult } from './tools';
import { drawCalls, registryOf } from './tools';
import type { Drawn } from './tools';

export const TARGET_KINDS = ['none', 'area_under', 'area_between', 'area_polygon', 'length', 'gradient', 'angle'] as const;
export type TargetKind = typeof TARGET_KINDS[number];
export interface Target { kind: TargetKind; curve: string; other: string; from: number | string | null; to: number | string | null; points: string[]; line: string }

export const TARGET_SCHEMA = {
  type: 'object',
  description: '문항이 구하라는 양. 엔진이 이 선언으로 측정 개체를 직접 세워 잰다(네가 measure 호출을 쓰지 않아도 된다). 이름은 풀이의 그림 선언과 같은 이름. area_under: 곡선 curve 와 x 축(other="xAxis") 사이, x 가 from 에서 to 까지. area_between: 두 곡선 curve, other 사이, from 에서 to 까지. from, to 는 수나 점 이름(그 점의 x 좌표). area_polygon: points 의 다각형 넓이. length: points 두 점 사이 길이. gradient: 선 line 의 기울기. angle: points 세 점(가운데가 꼭짓점)의 각. 구하는 양이 그림의 측정이 아니면 none',
  properties: {
    kind: { type: 'string', enum: [...TARGET_KINDS] },
    curve: { type: 'string', description: '곡선 이름. area_under, area_between 에서' },
    other: { type: 'string', description: 'area_under 는 "xAxis", area_between 은 다른 곡선이나 직선의 이름' },
    from: { anyOf: [{ type: 'number' }, { type: 'string' }, { type: 'null' }], description: '적분 하한. 수 또는 점 이름' },
    to: { anyOf: [{ type: 'number' }, { type: 'string' }, { type: 'null' }], description: '적분 상한. 수 또는 점 이름' },
    points: { type: 'array', items: { type: 'string' }, description: 'area_polygon 의 꼭짓점(둘레 순서), length 의 두 점, angle 의 세 점' },
    line: { type: 'string', description: 'gradient 의 선 이름' },
  },
  required: ['kind', 'curve', 'other', 'from', 'to', 'points', 'line'],
  additionalProperties: false,
};

export const NO_TARGET: Target = { kind: 'none', curve: '', other: '', from: null, to: null, points: [], line: '' };

export function readTarget(raw: unknown): Target {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const kind = (TARGET_KINDS as readonly string[]).includes(String(o.kind)) ? (o.kind as TargetKind) : 'none';
  const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const bound = (v: unknown): number | string | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() ? v.trim() : null);
  return { kind, curve: str(o.curve), other: str(o.other), from: bound(o.from), to: bound(o.to), points: Array.isArray(o.points) ? o.points.map((p) => String(p).trim()).filter(Boolean) : [], line: str(o.line) };
}

/** 세운 곡선의 식에서 y 의 우변을 뽑는다. y=…, f(x)=… 꼴만. 음함수(y^2=4x)는 못 뽑는다 */
export function rhsOf(results: ToolResult[], name: string): { rhs: string } | { problem: string } {
  const hit = results.find((r) => r.ok && r.call.function.name === 'algebraric' && (r.response as { name?: unknown } | undefined)?.name === name);
  if (!hit) return { problem: `${name} 은 algebraric 으로 세운 곡선이 아니다` };
  let expression = '';
  try { expression = String((JSON.parse(hit.call.function.arguments) as { expression?: unknown }).expression ?? ''); } catch { /* 아래에서 빈 식으로 처리 */ }
  if (!expression) return { problem: `${name} 의 식이 비었다` };
  const parts = expression.split('=');
  if (parts.length === 1) return { rhs: expression };
  if (parts.length === 2 && /^\s*(y|[a-zA-Z]\(x\))\s*$/.test(parts[0])) return { rhs: parts[1].trim() };
  return { problem: `${name} 의 식 "${expression}" 은 y=… 꼴이 아니라 정적분으로 못 바꾼다` };
}

function xOf(app: MathcoreApp, v: number | string | null): number | null {
  if (typeof v === 'number') return v;
  if (!v) return null;
  const n = Number(v);
  if (Number.isFinite(n)) return n;
  const o: any = registryOf(app).objectOf(v);
  const x = o?.position?.[0];
  return typeof x === 'number' && Number.isFinite(x) ? x : null;
}

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(4).replace(/0+$/, '').replace(/\.$/, ''));

/** 선언한 양을 엔진이 세워 잰다. 돌려주는 값은 세운 호출의 결과와 잰 값(넓이는 절댓값), 못 세우면 사유 */
export async function buildTarget(app: MathcoreApp, target: Target, results: ToolResult[], afterEach?: (names: string[]) => Promise<void>): Promise<{ drawn: Drawn | null; values: { kind: string; value: number }[]; note: string }> {
  const nothing = (note: string) => ({ drawn: null, values: [], note });
  if (target.kind === 'none') return nothing('구하는 양이 측정이 아니다');
  const calls: { tool: string; args: Record<string, unknown> }[] = [];
  let kindLabel = '';
  if (target.kind === 'area_under' || target.kind === 'area_between') {
    const a = rhsOf(results, target.curve);
    if ('problem' in a) return nothing(a.problem);
    let b = '0';
    if (target.kind === 'area_between') { const r = rhsOf(results, target.other); if ('problem' in r) return nothing(r.problem); b = r.rhs; }
    const lo = xOf(app, target.from); const hi = xOf(app, target.to);
    if (lo === null || hi === null) return nothing(`적분 구간을 정하지 못했다(from ${String(target.from)}, to ${String(target.to)})`);
    calls.push({ tool: 'algebraric', args: { expression: `\\int_{${fmt(Math.min(lo, hi))}}^{${fmt(Math.max(lo, hi))}}((${a.rhs})-(${b}))dx`, name: 'S', condition: null } });
    kindLabel = '넓이(정적분)';
  } else if (target.kind === 'area_polygon') {
    if (target.points.length < 3) return nothing('다각형 꼭짓점이 셋 미만이다');
    calls.push({ tool: 'polygon.polygon', args: { pointIDs: target.points, name: 'm1' } }, { tool: 'measure.area', args: { objectID: 'm1', name: 'm2' } });
    kindLabel = '넓이';
  } else if (target.kind === 'length') {
    if (target.points.length !== 2) return nothing('길이는 두 점이 필요하다');
    calls.push({ tool: 'measure.lengthByTwoPointsID', args: { pointID1: target.points[0], pointID2: target.points[1], name: 'm1' } });
    kindLabel = '길이';
  } else if (target.kind === 'gradient') {
    if (!target.line) return nothing('기울기는 선 이름이 필요하다');
    calls.push({ tool: 'measure.gradient', args: { linearID: target.line, name: 'm1' } });
    kindLabel = '기울기';
  } else if (target.kind === 'angle') {
    if (target.points.length !== 3) return nothing('각은 세 점이 필요하다');
    calls.push({ tool: 'measure.angle', args: { pointID1: target.points[0], pointID2: target.points[1], pointID3: target.points[2] } });
    kindLabel = '각';
  }
  const before = new Set(readMeasurements(app).map((m) => m.id));
  const drawn = await drawCalls(app, { symbols: [], calls }, true, undefined, [], afterEach);
  const fresh = readMeasurements(app).filter((m) => !before.has(m.id));
  const values = fresh.flatMap((m) => (kindLabel.startsWith('넓이') && m.value < 0 ? [{ kind: `${kindLabel} 절댓값`, value: Math.abs(m.value) }] : [{ kind: kindLabel, value: m.value }]));
  const failed = drawn.results.filter((r) => !r.ok).map((r) => r.detail);
  return { drawn, values, note: failed.length ? `엔진 측정 실패: ${failed.join(' / ')}` : `엔진이 ${kindLabel}를 세워 잼: ${calls.map((c) => `${c.tool} ${JSON.stringify(c.args)}`).join(' | ')}` };
}
